/**
 * Common Zod Schemas
 *
 * Reusable schema components for validation.
 */

import { z } from 'zod';
import { QUEUE_LIMITS } from '../constants/defaults.js';

// =============================================================================
// Primitive Schemas
// =============================================================================

/**
 * Session ID - non-empty string
 */
export const SessionIdSchema = z.string().min(1, 'Session ID is required');

/**
 * Agent ID - non-empty string
 */
export const AgentIdSchema = z.string().min(1, 'Agent ID is required');

/**
 * Tool Use ID - non-empty string
 */
export const ToolUseIdSchema = z.string().min(1, 'Tool Use ID is required');

/**
 * SDK Session ID - non-empty string
 */
export const SdkSessionIdSchema = z.string().min(1, 'SDK Session ID is required');

// =============================================================================
// Permission Mode Schema
// =============================================================================

/**
 * K6 P3 C0: widened to match the full SDK PermissionMode union (v0.3.177).
 * Added `'plan'` and `'auto'` — additive; existing sessions that used the
 * previous 4-value enum still parse without error.
 *
 * NOTE: `'auto'` is included for SDK round-trip fidelity but is not exposed
 * in the Vestra UI.  The session-level `PermissionMode` type in types/session.ts
 * keeps the original 4-value form for now; a follow-up will migrate it.
 */
export const PermissionModeSchema = z.enum([
  'default',
  'acceptEdits',
  'dontAsk',
  'bypassPermissions',
  // K6 P3 C0 additions (SDK-aligned):
  'plan',
  'auto',
]);

// =============================================================================
// Access Level Schema (Permissions v2 — Phase A)
// =============================================================================

/**
 * The four-member human access dial (Permissions v2): the single knob for how much
 * the agent may do on its own. Strictness order careful < balanced < relaxed <
 * autopilot; 'relaxed' is the default and is byte-identical to today's chill mode.
 * A SEPARATE axis from PermissionMode (SDK modes) — the two never write each other.
 *
 * NOTE: this enum is intentionally duplicated from the agent-sdk policy module.
 * A2 defines it locally in shared to stay independent of the parallel A1 branch;
 * a follow-up dedupes the two definitions.
 */
export const AccessLevelSchema = z.enum(['careful', 'balanced', 'relaxed', 'autopilot']);
export type AccessLevel = z.infer<typeof AccessLevelSchema>;

/** Body of the settings write that sets the user's global default access level. */
export const AccessLevelSettingsSchema = z.object({ accessLevel: AccessLevelSchema });
export type AccessLevelSettings = z.infer<typeof AccessLevelSettingsSchema>;

// =============================================================================
// Permission Behavior Schema (K6 P3 C0)
// =============================================================================

/**
 * Zod schema for `PermissionBehavior` — the verdict a permission rule carries.
 * Mirrors SDK `PermissionBehavior` (v0.3.177).
 */
export const PermissionBehaviorSchema = z.enum(['allow', 'deny', 'ask']);

// =============================================================================
// Permission Rule Value Schema (K6 P3 C0)
// =============================================================================

/**
 * Zod schema for `PermissionRuleValue` — a single tool/pattern rule entry.
 * Mirrors SDK `PermissionRuleValue` (v0.3.177).
 */
export const PermissionRuleValueSchema = z.object({
  toolName: z.string().min(1),
  ruleContent: z.string().optional(),
});

// =============================================================================
// Permission Update Destination Schema (K6 P3 C0)
// =============================================================================

/**
 * Zod schema for `PermissionUpdateDestination`.
 * Mirrors SDK `PermissionUpdateDestination` (v0.3.177).
 */
export const PermissionUpdateDestinationSchema = z.enum([
  'userSettings',
  'projectSettings',
  'localSettings',
  'session',
  'cliArg',
]);

// =============================================================================
// Permission Update Schema (K6 P3 C0)
// =============================================================================

/**
 * Zod schema for `PermissionUpdate` — the discriminated union of permission
 * mutations.  Mirrors SDK `PermissionUpdate` (v0.3.177), all 6 variants.
 */
export const PermissionUpdateSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('addRules'),
    rules: z.array(PermissionRuleValueSchema),
    behavior: PermissionBehaviorSchema,
    destination: PermissionUpdateDestinationSchema,
  }),
  z.object({
    type: z.literal('replaceRules'),
    rules: z.array(PermissionRuleValueSchema),
    behavior: PermissionBehaviorSchema,
    destination: PermissionUpdateDestinationSchema,
  }),
  z.object({
    type: z.literal('removeRules'),
    rules: z.array(PermissionRuleValueSchema),
    behavior: PermissionBehaviorSchema,
    destination: PermissionUpdateDestinationSchema,
  }),
  z.object({
    type: z.literal('setMode'),
    mode: PermissionModeSchema,
    destination: PermissionUpdateDestinationSchema,
  }),
  z.object({
    type: z.literal('addDirectories'),
    directories: z.array(z.string()),
    destination: PermissionUpdateDestinationSchema,
  }),
  z.object({
    type: z.literal('removeDirectories'),
    directories: z.array(z.string()),
    destination: PermissionUpdateDestinationSchema,
  }),
]);

// =============================================================================
// Session Status Schema
// =============================================================================

export const SessionStatusSchema = z.enum(['active', 'paused', 'completed', 'error']);

// =============================================================================
// Image Schemas
// =============================================================================

export const ImageMediaTypeSchema = z.enum([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

// PERF: Max base64 string length for 10MB image (base64 adds ~33% overhead)
const MAX_BASE64_LENGTH = Math.ceil(QUEUE_LIMITS.MAX_SINGLE_IMAGE_BYTES * 4 / 3);

export const ImageAttachmentSchema = z.object({
  data: z
    .string()
    .min(1, 'Image data is required')
    // PERF: Validate size to prevent memory exhaustion
    .max(MAX_BASE64_LENGTH, `Image too large (max ${QUEUE_LIMITS.MAX_SINGLE_IMAGE_BYTES / 1024 / 1024}MB)`)
    // Validate base64 format (alphanumeric, +, /, and optional = padding)
    .refine(
      (data) => /^[A-Za-z0-9+/]*={0,2}$/.test(data),
      'Invalid base64 format'
    ),
  mediaType: ImageMediaTypeSchema,
  // Display name minted client-side (the only truthful local clock for
  // "Pasted image <date> at <time>"). Server sanitizes before storage.
  filename: z.string().min(1).max(200).optional(),
});

export const FileAttachmentSchema = z.object({
  filename: z.string(),
  fileId: z.string().uuid().optional(),
  // Must be a well-formed URL (defense-in-depth). The server never fetches it:
  // it reads the file id (this field, else the id in the URL's object path) and
  // the gate decides (syncFileAttachmentsToVolume).
  url: z.string().url(),
  mediaType: z.string(),
  sizeBytes: z.number(),
});

// =============================================================================
// Question Schemas
// =============================================================================

export const QuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
});

export const QuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(QuestionOptionSchema),
  multiSelect: z.boolean(),
});

// =============================================================================
// Todo Schemas
// =============================================================================

export const TodoStatusSchema = z.enum(['pending', 'in_progress', 'completed']);

export const TodoSchema = z.object({
  content: z.string(),
  status: TodoStatusSchema,
  // Optional on the wire: the runner's TodoCreate takes activeForm as optional and
  // its snapshot omits the key when unset (apps/sandbox task-tools.ts toWireSnapshot);
  // every web reader already falls back to `content`.
  activeForm: z.string().optional(),
  // §4.7 (the board) — the roster handle holding this item. Optional: an item
  // nobody holds parses exactly as it did before the board existed, and a zod
  // object strips what it does not declare, so this key is the wire's gate.
  owner: z.string().optional(),
});

// =============================================================================
// Serialized Session Schema
// =============================================================================

export const SerializedSessionSchema = z.object({
  id: SessionIdSchema,
  sdkSessionId: z.string().optional(),
  agentId: AgentIdSchema,
  status: SessionStatusSchema,
  permissionMode: PermissionModeSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// =============================================================================
// Type Exports
// =============================================================================

export type PermissionModeInput = z.input<typeof PermissionModeSchema>;
export type SessionStatusInput = z.input<typeof SessionStatusSchema>;
export type ImageMediaTypeInput = z.input<typeof ImageMediaTypeSchema>;
export type ImageAttachmentInput = z.input<typeof ImageAttachmentSchema>;
export type QuestionOptionInput = z.input<typeof QuestionOptionSchema>;
export type QuestionInput = z.input<typeof QuestionSchema>;
export type TodoInput = z.input<typeof TodoSchema>;
export type SerializedSessionInput = z.input<typeof SerializedSessionSchema>;
// K6 P3 C0 — permission schema inferred types
export type PermissionBehaviorInput = z.input<typeof PermissionBehaviorSchema>;
export type PermissionRuleValueInput = z.input<typeof PermissionRuleValueSchema>;
export type PermissionUpdateDestinationInput = z.input<typeof PermissionUpdateDestinationSchema>;
export type PermissionUpdateInput = z.input<typeof PermissionUpdateSchema>;
